import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchServerTime, login, logout } from "../apis/authService";
import {
  IAuthState,
  ILoginCredentials,
  VerifyOtpPayload,
} from "../interfaces/IAuth";
import { StatusCodes } from "http-status-codes";
import { jwtDecode } from "jwt-decode";
import { RootState } from "../store/store";
import { loginDetails } from "./hospitalSlice";
import { AxiosError } from "axios";
import Axios from "../utils/axiosInterceptor";
import { AUTH_APIS } from "../utils/constants";
import axios from "axios";
import { broadcastBillAuditLogout } from "../utils/billAuditSession";

const initialState: IAuthState = {
  userEmail: null,
  userName: null,
  role: {
    AccessModules: null,
    id: null,
    role: null,
  },
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  loading: false,
  error: null,
  serverTime: null,
  accessModules: null,
};

// export const selectAccessToken = (state: RootState) => state.auth.accessToken;

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials: ILoginCredentials, { dispatch, rejectWithValue }) => {
    try {
      const resp = await login(credentials);
      if (resp.status === StatusCodes.OK) {
        // localStorage.setItem(
        //   "edgeProAccessToken",
        //   JSON.stringify(resp.authData.access_token)
        // );
        // localStorage.setItem(
        //   "edgeProRefreshToken",
        //   JSON.stringify(resp.authData.refresh_token)
        // );
        dispatch(loginDetails(resp));

        // Update server time here
        if (resp.authData.access_token) {
          dispatch(getServerTime({ accessToken: resp.authData.access_token }));
        }

        return {
          accessToken: resp.authData.access_token,
          refreshToken: resp.authData.refresh_token,
          user: credentials.email,
          qrCode: resp?.authData?.user?.qrCode,
          accessModules: resp.authData.user?.role?.accessModules,
          role: resp?.authData?.user?.role,
          userName:
            resp?.authData?.user?.firstname && resp?.authData?.user?.lastname
              ? resp?.authData?.user?.firstname +
                " " +
                resp?.authData?.user?.lastname
              : resp?.authData?.user?.firstname
              ? resp?.authData?.user?.firstname
              : null,
        };
      } else {
        return rejectWithValue(resp.authData);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ code, email }: VerifyOtpPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(AUTH_APIS.VERIFY_OTP, { email, code });
      const token = response.data?.access_token;
      if (token) {
        dispatch(getServerTime({ accessToken: token }));
      }
      return response.data;
    } catch (err) {
      const error = err as AxiosError;
      return rejectWithValue("OTP verification failed");
    }
  }
);

export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  broadcastBillAuditLogout();
  await logout();
  localStorage.removeItem("edgeProAccessToken");
  localStorage.removeItem("edgeProRefreshToken");
  localStorage.removeItem("userName");
});

export const verifyUserTokens = createAsyncThunk(
  "auth/verifyUserToken",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const accessToken = localStorage.getItem("edgeProAccessToken");
      const refreshToken = localStorage.getItem("edgeProRefreshToken");
      const userName = localStorage.getItem("userName");
      const role = localStorage?.getItem("role");
      const userStr = localStorage.getItem("user");

      if (!refreshToken || !accessToken) {
        throw new Error("No auth data found in local storage");
      }
      const userData = jwtDecode(JSON.parse(accessToken));

      // Update server time here
      if (accessToken) {
        dispatch(getServerTime({ accessToken: JSON.parse(accessToken) }));
      }
      return {
        accessToken: JSON.parse(accessToken),
        refreshToken: JSON.parse(refreshToken),
        userName: userName,
        role: role,
        user: userStr ? JSON.parse(userStr) : null,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getServerTime = createAsyncThunk(
  "auth/getServerTime",
  async ({ accessToken }: { accessToken: string }) => {
    const response = await fetchServerTime(accessToken);
    return response;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        // state.accessToken = action.payload.accessToken;
        // state.refreshToken = action.payload.refreshToken;
        state.userEmail = action.payload.user;
        state.role = action.payload.role;
        state.accessModules = action.payload.accessModules;
        state.userName = action.payload.userName;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        const user = action.payload.user;
        state.userEmail = typeof user === "string" ? user : user?.email ?? null;
        state.role = user?.role ?? action.payload.role ?? state.role;
        state.accessModules =
          user?.role?.accessModules ?? action.payload.accessModules ?? null;
        state.userName =
          user?.firstname && user?.lastname
            ? `${user.firstname} ${user.lastname}`
            : user?.firstname ?? state.userName;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = (action.payload as string) || "Invalid email or password";
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.accessToken = null;
        state.refreshToken = null;
        state.userEmail = null;
        state.userName = null;
      })
      .addCase(getServerTime.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getServerTime.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.serverTime = action.payload;
        }
      )
      .addCase(verifyUserTokens.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyUserTokens.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;

        // Initialize role object if it doesn't exist
        if (!state.role) {
          state.role = {
            AccessModules: null,
            id: null,
            role: null,
          };
        }

        const userRole = action?.payload?.user?.role;
        if (userRole) {
          state.role.AccessModules = userRole.accessModules;
          state.role.id = userRole.id;
          state.role.role = userRole.role;
        }

        state.userEmail = action.payload.user as string;
        state.userName = action.payload.userName;
      })
      .addCase(verifyUserTokens.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.accessToken = null;
        state.refreshToken = null;
        state.userEmail = null;
        state.userName = null;
      });
  },
});

export const selectAccessToken = (state: RootState) => state.auth.accessToken;
// export const selectAccessModules = (state: RootState) =>
//   state.auth.accessModules;

export default authSlice.reducer;
